import React, { useContext, useLayoutEffect, useMemo, useRef } from 'react';

import { Grid, Layout, Menu, theme } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import type { MenuProps } from 'antd';
import { useTheme as useThemeStyledComponents } from 'styled-components';
import { authContext } from '@/contexts/Auth';
import { useFlags } from '@/contexts/Flags';
import ShoppbudCollapsed from '@/assets/images/shoppbud.svg';
import ShoppbudExpand from '@/assets/images/shoppbud-v2-dark.svg';
import useGetMe from '@/store/Sessions/useGetMe';
import { useTheme } from '@/contexts/Theme';
import env from '@/env';
import { hasChurnAccessByEmail } from '@/utils/churnAccess';
import { hasCustomerManagementAccess } from '@/utils/customerManagementAccess';
import { resolveRemoteConfig } from '@/utils/resolveRemoteConfig';
import { MenuScrollHidden } from './styles';
import { rawMenu } from './menuSchema';
import { buildMenu } from './buildMenu';
import { useMenu } from '../../../contexts/Menu';

const { Sider } = Layout;

const { useToken } = theme;

const SideBar: React.FC = () => {
    const { isCollapsed, setCollapsed } = useMenu();

    const { useBreakpoint } = Grid;

    const screens = useBreakpoint();
    const isMobile = !screens.xl;
    const previousIsMobileRef = useRef<boolean | null>(null);

    useLayoutEffect(() => {
        if (previousIsMobileRef.current === null) {
            setCollapsed(isMobile);
            previousIsMobileRef.current = isMobile;
            return;
        }

        if (previousIsMobileRef.current !== isMobile) {
            setCollapsed(isMobile);
            previousIsMobileRef.current = isMobile;
        }
    }, [isMobile]);

    const { theme } = useTheme();

    const { colors } = useThemeStyledComponents();

    const { token } = useToken();

    const navigate = useNavigate();
    const location = useLocation();

    const siderStyle: React.CSSProperties = {
        height: '100vh',
        position: 'sticky',
        insetInlineStart: 0,
        top: 0,
        bottom: 0,
        scrollbarWidth: 'thin',
        scrollbarGutter: 'stable',
        // eslint-disable-next-line prettier/prettier
        borderRight: `1px solid ${theme === 'dark' ? token.colorBorder : colors.lightGrey}`,
        ...(isMobile && {
            position: 'fixed',
            left: 0,
            zIndex: 1200,
        }),
    };

    const { data, isAssuming } = useContext(authContext);
    const { data: extUser } = useGetMe();
    const flags = useFlags();
    const conditions = useMemo(() => {
        return {
            canChangeTaxData: !!(
                extUser?.shopManager?.can_change_tax_data && !isAssuming
            ),
            emitInvoice: !!data.user?.shopManager?.emit_invoice,
            hasChurnAccess: hasChurnAccessByEmail(
                flags?.USERS_TO_ACCESS_SCREEN_CHURN,
                env.ENV,
                data?.user?.email,
            ),
            quickActionsEnabled: resolveRemoteConfig(
                flags.ENABLE_QUICK_ACTIONS,
            ),
            sellStoreEnabled: resolveRemoteConfig(flags.ENABLE_SELL_STORE),
            allowCustomerManagement: hasCustomerManagementAccess(
                isAssuming,
                extUser?.shopManager?.allow_customer_management_view,
            ),
        };
    }, [
        extUser,
        isAssuming,
        data?.user?.email,
        data.user?.shopManager?.emit_invoice,
        flags?.USERS_TO_ACCESS_SCREEN_CHURN,
        flags?.ENABLE_QUICK_ACTIONS,
        flags?.ENABLE_SELL_STORE,
    ]);

    const items = useMemo(
        () => buildMenu(rawMenu(flags, conditions), data.role),
        [data.role, flags, conditions],
    );

    const onClick: MenuProps['onClick'] = (e) => {
        if (/^https?:\/\//.test(e.key)) {
            window.open(e.key, '_blank', 'noopener,noreferrer');
            if (isMobile) setCollapsed(true);
            return;
        }

        navigate(e.key);
        if (isMobile) setCollapsed(true);
    };

    const selectedKeys = useMemo(() => {
        if (location.pathname === '/') {
            return ['/'];
        }

        const paths = location.pathname.split('/').filter(Boolean);

        return paths.map(
            (_, index) => `/${paths.slice(0, index + 1).join('/')}`,
        );
    }, [location.pathname]);

    return (
        <Sider
            collapsible
            collapsed={isCollapsed}
            onCollapse={(value) => setCollapsed(value)}
            theme="light"
            style={siderStyle}
            width={249}
            breakpoint={isMobile ? 'xl' : undefined}
            collapsedWidth={isMobile ? 0 : undefined}
            zeroWidthTriggerStyle={{
                top: 10,
            }}
        >
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '16px 10px',
                }}
            >
                <img
                    src={isCollapsed ? ShoppbudCollapsed : ShoppbudExpand}
                    alt="shoppbud"
                    width={isCollapsed ? 30 : 140}
                    height={25}
                />
            </div>
            <MenuScrollHidden>
                <Menu
                    theme="light"
                    defaultSelectedKeys={['1']}
                    selectedKeys={selectedKeys}
                    mode="inline"
                    items={items}
                    onClick={onClick}
                    style={{ border: 'none' }}
                />
            </MenuScrollHidden>
        </Sider>
    );
};

export default SideBar;
